// @flow
import React, {useState, useEffect, useMemo} from 'react';
import {Divider, IconButton, makeStyles, Typography} from '@material-ui/core';
import {ArrowBack} from '@material-ui/icons';
import {Alert} from '@material-ui/lab';
import {colors} from '../../../../assets/styles/colors';
import {Link, Route, Switch, useLocation} from 'react-router-dom';

import ConfigureTagButton from '../../components/ConfigureTagButton';
import LogisticShipping from '../logistic-shipping/LogisticShipping';
import {usePage} from '../../../page/utils';
import LogisticsPickup from '../logistic-pickup/LogisticsPickup';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import {FormControlLabel} from '@material-ui/core';
import LogisticPromotions from '../logistic-promotions/LogisticPromotions';
import {useEnabledLogisticShipping} from './utils';
import {
  useEnabledLogisticPickup,
  useUpdateEnabledPickupMethod,
} from '../logistic-pickup/utils';
import {useCRUDPromotion} from '../logistic-promotions/utils';

type SubRoute = {
  name: string,
  path: string,
  enabled: boolean,
  description?: string,
  disabled?: boolean,
  component?: () => any,
  secondLine?: string,
  disabledMinimal?: boolean,
  children?: Node,
  id?: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2),
    },

    borderRadius: '12px',
  },
  wrapperTagButton: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    boxShadow:
      '1px 0px 1px rgba(40, 41, 61, 0.08), -1px 0px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  logisticsButton: {
    margin: theme.spacing(0, -3),
    borderRadius: '0px',
    padding: theme.spacing(3),
    textAlign: 'left',
    justifyContent: 'flex-start',
    maxHeight: 76,
  },
  startIcon: {
    marginRight: theme.spacing(2),
  },
  endIcon: {
    marginLeft: 'auto',
    color: colors.grey800,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  dividerRoot: {
    width: '100%',
    background: colors.grey200,
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(2),
    backgroundColor: 'white',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    borderLeft: '1px solid #dbdbe0',
    borderRight: '1px solid #dbdbe0',
    borderTop: '1px solid #dbdbe0',
  },
  subRouteHeaderNoBorder: {
    display: 'flex',
    alignItems: 'center',
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(2),
    backgroundColor: 'white',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  subRouteDescription: {
    marginLeft: theme.spacing(1),
    fontSize: '0.85rem',
    color: colors.grey800,
  },
  between: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    // backgroundColor: 'white',
    border: '1px solid #dbdbe0',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
  },
  switchContainer: {
    marginRight: theme.spacing(1),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
}));

export default usePage(Logistics, 'Logistics Configuration');

function Logistics() {
  const classes = useStyles();
  const location = useLocation();

  const [enabledPickup, setEnabledPickup] = useState(false);
  const [enabledPromotions, setEnabledPromotions] = useState(false);
  const {deliveryMethods, getDeliveryMethods} = useEnabledLogisticShipping();
  const {pickupMethod} = useEnabledLogisticPickup();
  const {
    updatePickupMethod,
    isSaving,
    savingEnabledError,
    savingEnabledSuccess,
  } = useUpdateEnabledPickupMethod();
  const {
    updatePromotion,
    isSaving: isSavingPromotions,
    savingPromotionError,
    savingPromotionSuccess,
  } = useCRUDPromotion();

  const handleTogglePickup = () => {
    // setEnabledPickup(!enabledPickup);
    updatePickupMethod(!enabledPickup);
  };

  const handleTogglePromotions = () => {
    // setEnabledPromotions(!enabledPromotions);
    // $FlowFixMe
    updatePromotion({
      enabledPromotions: !enabledPromotions,
    });
  };

  useEffect(() => {
    if (pickupMethod) {
      setEnabledPickup(pickupMethod.enabled);
    }
  }, [pickupMethod]);

  useEffect(() => {
    if (deliveryMethods) {
      setEnabledPromotions(deliveryMethods?.shippingMethod?.enabledPromotions);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMethods?.shippingMethod?.enabledPromotions]);

  useEffect(() => {
    if (savingEnabledError) {
      setEnabledPickup(enabledPickup);
    }
  }, [savingEnabledError, enabledPickup]);

  useEffect(() => {
    if (savingPromotionError) {
      setEnabledPromotions(enabledPromotions);
    }
  }, [savingPromotionError, enabledPromotions]);

  useEffect(() => {
    if (savingEnabledSuccess) {
      setEnabledPickup(!enabledPickup);
    }
  }, [savingEnabledSuccess, enabledPickup]);

  useEffect(() => {
    if (savingPromotionSuccess) {
      setEnabledPromotions(!enabledPromotions);
    }
  }, [savingPromotionSuccess, enabledPromotions]);

  useEffect(() => {
    getDeliveryMethods();
  }, [getDeliveryMethods]);

  const subRoutes: {[key: string]: SubRoute} = useMemo(() => {
    const subRoutes: {[key: string]: any} = {
      shipping: {
        name: 'Tarifas de envío',
        path: '/configure/logistics/shipping',
        component: LogisticShipping,
        enabled: deliveryMethods && deliveryMethods?.shippingMethod?.enabled,
        id: 'shipping-cost',
        disabled: false,
      },
      pickup: {
        name: 'Recojo en tienda',
        path: '/configure/logistics/pickup',
        description:
          'Crea promociones de envío en base a la cantidad de compra.',
        component: LogisticsPickup,
        enabled: deliveryMethods && deliveryMethods?.pickupMethod?.enabled,
        id: 'pickup',
        disabled: false,
      },
      promotions: {
        name: 'Promociones de envío',
        path: '/configure/logistics/promotions',
        description:
          'Crea promociones de envío en base a la cantidad de compra.',
        component: LogisticPromotions,
        enabled: enabledPromotions,
        id: 'shipping-promotions',
        disabled: false,
      },
    };
    subRoutes.integrated = {
      name: 'Servicio de envíos integrado',
      path: '/configure/logistics/integrated',
      component: () => null,
      enabled: false,
      disabled: true,
      secondLine: 'Pronto estará disponible',
    };
    return subRoutes;
  }, [deliveryMethods, enabledPromotions]);

  const subRoutesArray = Object.keys(subRoutes).map(k => subRoutes[k]);

  const subRoute: ?SubRoute = subRoutesArray.find(
    (route: SubRoute) => location.pathname === route.path
  );

  return (
    <div className={classes.root}>
      {subRoute && (
        <>
          {subRoute.id === 'shipping-promotions' && (
            <Alert severity="info" className={classes.alert}>
              Las promociones solo pueden ser activadas si el método de envío no
              es "Definir al momento de la compra".
            </Alert>
          )}

          <div
            className={`${
              subRoute.id === 'pickup' || subRoute.id === 'shipping-promotions'
                ? classes.between
                : ''
            }`}
          >
            <div
              className={`${
                subRoute.id === 'pickup' ||
                subRoute.id === 'shipping-promotions'
                  ? classes.subRouteHeaderNoBorder
                  : classes.subRouteHeader
              }`}
            >
              <IconButton
                variant="contained"
                size="small"
                component={Link}
                to="/configure/logistics"
              >
                <ArrowBack />
              </IconButton>
              <div>
                <Typography className={classes.subRouteTitle}>
                  {subRoute.name}
                </Typography>
                {subRoute.description && (
                  <Typography className={classes.subRouteDescription}>
                    {subRoute.description}
                  </Typography>
                )}
              </div>
            </div>
            {subRoute.id === 'pickup' && (
              <FormControlLabel
                disabled={isSaving}
                className={classes.switchContainer}
                control={
                  <IosSwitch
                    onChange={handleTogglePickup}
                    checked={enabledPickup}
                    name="enabledPickup"
                  />
                }
                labelPlacement="start"
                label={
                  <div className={classes.label}>Activar recojo en tienda</div>
                }
              />
            )}
            {subRoute.id === 'shipping-promotions' && (
              <FormControlLabel
                disabled={
                  isSavingPromotions ||
                  deliveryMethods?.shippingMethod?.storeShippingMethodType ===
                    'MANUAL'
                }
                className={classes.switchContainer}
                control={
                  <IosSwitch
                    onChange={handleTogglePromotions}
                    checked={enabledPromotions}
                    name="enabledPromotions"
                  />
                }
                labelPlacement="start"
                label={<div className={classes.label}>Activar Promociones</div>}
              />
            )}
          </div>
        </>
      )}

      <Switch>
        <Route exact path="/configure/logistics">
          <div className={classes.wrapperTagButton}>
            {subRoutesArray.map((route: SubRoute, idx: number) => (
              <React.Fragment key={`logisticsButton-${idx}`}>
                <ConfigureTagButton
                  enabled={route.enabled}
                  disabled={route.disabled}
                  path={route.path}
                  label={route.name}
                  secondLine={route.secondLine}
                  img={null}
                  id={route.id}
                >
                  {
                    // $FlowFixMe
                    route.children
                  }
                </ConfigureTagButton>
                {idx < subRoutesArray.length && (
                  <Divider className={classes.dividerRoot} />
                )}
              </React.Fragment>
            ))}
          </div>
        </Route>
        {subRoutesArray.map((route: SubRoute, idx: number) => (
          <Route
            key={`logistics-route-${idx}`}
            path={route.path}
            component={route.component}
          />
        ))}
      </Switch>
    </div>
  );
}
